import React from 'react'

import get from 'lodash/get'
import isNil from 'lodash/isNil'

import { Head, ITableHead } from './head'
import TableData from './tableData'
import Loader from 'components/ui/loader'

export interface ITableConfig {
  dataSetName?: string
  head: ITableHead[]
  borderBottom?: boolean
}

export interface IDataObject {
  value: string | number
  unit?: string
}

export interface IData {
  [k: string]: IDataObject | string | number
}

interface IProps {
  config: ITableConfig
  isLoading?: boolean
  data: IData[] | null
}

class Table<T extends IProps, S> extends React.Component<T, S> {
  /**
   * Returns the value of an attribute from a IData item
   *
   * @param {IData} item - A single row of the table
   * @param {string} key - The key of the attribute
   * @return {string | number | undefined} The value of the attribute
   *
   */
  getValue = (item: IData, key: string) => {
    if (item && !isNil(item[key])) {
      if (item[key].hasOwnProperty('value')) {
        const value = (item[key] as IDataObject).value
        if (!isNil(value)) {
          return value
        }
        return undefined
      }
      if (!isNil(item[key])) {
        return String(item[key])
      }
      return undefined
    }
    return undefined
  }

  /**
   * Returns the unit of an attribute from a IData item
   *
   * @param {IData} item - A single row of the table
   * @param {string} key - The key of the attribute
   * @return {string | undefined} The unit of the attribute
   *
   */
  getUnit = (item: IData, key: string) => {
    if (item && !isNil(item[key])) {
      if (item[key].hasOwnProperty('unit')) {
        return (item[key] as IDataObject).unit
      }
      return undefined
    }
    return undefined
  }

  /**
   * Returns the unit defined in the table head
   *
   * @param {ITableHead} headItem - A single head item
   * @return {string | undefined} The unit of the head item
   *
   */
  getHeadUnit = (headItem: ITableHead) => {
    if (headItem && !isNil(headItem.unit)) {
      return headItem.unit
    }
    return undefined
  }

  public render() {
    const { data, isLoading } = this.props
    const { head } = this.props.config

    const tableData = (
      <div>
        {data && data.length ? (
          data.map((elem: IData, index: number) => {
            return (
              <div key={index} className="flex">
                {head.map((headItem: ITableHead) => {
                  return (
                    <TableData
                      key={headItem.key}
                      name={headItem.key}
                      col={headItem.col}
                      hideUnit={get(headItem, 'hideUnit', undefined)}
                      text={this.getValue(elem, headItem.key)}
                      unit={this.getUnit(elem, headItem.key) || this.getHeadUnit(headItem)}
                      borderBottom={index === data.length - 1}
                    />
                  )
                })}
              </div>
            )
          })
        ) : (
          <p className="w-full text-black text-13 font-400 tracking-0.1">
            Es sind keine Einträge vorhanden.
          </p>
        )}
      </div>
    )

    return (
      <div className="w-full mb-10 px-48">
        <Head head={head} />
        {isLoading ? <Loader /> : tableData}
      </div>
    )
  }
}

export default Table
